import { AUDIO_URLS, MASTER_VOLUME, MUSIC_VOLUME, SFX_VOLUME, type SfxKey } from '@/config/audio';

type Nullable<T> = T | null;

interface AudioEngine {
  unlock(): Promise<void>;
  playSfx(key: SfxKey): void;
  startBgm(): Promise<void>;
  stopBgm(): void;
  handleVisibility(hidden: boolean): void;
  setMuted(muted: boolean): void;
  isMuted(): boolean;
}

let singleton: Nullable<AudioEngine> = null;

export function getAudioEngine(): AudioEngine {
  if (singleton) return singleton;

  // Внутреннее состояние аудио‑движка
  let ctx: Nullable<AudioContext> = null;
  let unlocked = false;

  const sfxBuffers: Partial<Record<SfxKey, AudioBuffer>> = {};
  const sfxLastPlayMs: Partial<Record<SfxKey, number>> = {};

  let bgmBuffer: Nullable<AudioBuffer> = null;
  let bgmSource: Nullable<AudioBufferSourceNode> = null;
  let bgmGain: Nullable<GainNode> = null;
  let bgmPlaying = false;
  let bgmShouldPlay = false;
  let gestureFallbackInstalled = false;
  let masterMuted = false;

  // Инициализация флага mute из persistent-хранилища
  try {
    const saved = localStorage.getItem('audio:muted');
    if (saved != null) {
      masterMuted = saved === '1' || saved === 'true';
    }
    } catch {
      // Игнорируем ошибки доступа к хранилищу (приватный режим, запрет storage и т.п.)
    }

  function ensureCtx(): AudioContext {
    if (!ctx) {
      // @ts-expect-error: webkitAudioContext доступен только в некоторых браузерах (iOS Safari)
      const AC = window.AudioContext || window.webkitAudioContext;
      ctx = new AC();
    }
    return ctx;
  }

  async function resumeCtx(): Promise<void> {
    const c = ensureCtx();
    if (c.state === 'suspended') {
      try {
        await c.resume();
      } catch {
        // Игнорируем неудачный resume: звук просто останется в состоянии suspended
      }
    }
  }

  function installGestureUnlockFallback(): void {
    if (gestureFallbackInstalled) return;
    gestureFallbackInstalled = true;

    const pointerOpts: AddEventListenerOptions = { passive: true, capture: true };
    const keydownCapture = true;

    const cleanup = () => {
      window.removeEventListener('pointerdown', tryResume, pointerOpts);
      window.removeEventListener('pointerup', tryResume, pointerOpts);
      window.removeEventListener('touchstart', tryResume, pointerOpts);
      window.removeEventListener('touchend', tryResume, pointerOpts);
      window.removeEventListener('keydown', onKeyDown, keydownCapture);
    };

    const afterRunning = () => {
      // Дополнительный «тихий пинг», чтобы стабилизировать вывод звука
      try {
        const c = ensureCtx();
        const silentBuffer = c.createBuffer(1, 1, Math.max(22050, c.sampleRate));
        const src = c.createBufferSource();
        src.buffer = silentBuffer;
        const gain = c.createGain();
        gain.gain.value = 0.00001;
        src.connect(gain);
        gain.connect(c.destination);
        try {
          src.start(0);
        } catch {
          // Игнорируем сбой запуска «тихого» буфера
        }
        try {
          src.stop(0);
        } catch {
          // Игнорируем сбой остановки «тихого» буфера
        }
      } catch {
        // Даже если «тихий пинг» не удался, считаем контекст разблокированным
      }

      // Если музыка была «заказана», запустим её
      if (bgmShouldPlay && !bgmPlaying) {
        void startBgmInternal();
      }
      cleanup();
    };

    const tryResume = () => {
      try {
        const c = ensureCtx();
        // Быстрый путь: если уже running
        if (c.state === 'running') {
          afterRunning();
          return;
        }
        // Иначе просим resume и проверим состояние по завершении
        void c
          .resume()
          .then(afterRunning)
          .catch(() => {
            // Игнорируем отказ в resume: жест пользователя может быть недостаточным для некоторых платформ
          });
      } catch {
        // Игнорируем прочие ошибки при попытке resume
      }
    };

    const onKeyDown = (): void => {
      tryResume();
    };

    window.addEventListener('pointerdown', tryResume, pointerOpts);
    window.addEventListener('pointerup', tryResume, pointerOpts);
    window.addEventListener('touchstart', tryResume, pointerOpts);
    window.addEventListener('touchend', tryResume, pointerOpts);
    window.addEventListener('keydown', onKeyDown, keydownCapture);
  }

  async function decodeUrl(url: string): Promise<AudioBuffer> {
    const c = ensureCtx();
    const res = await fetch(url);
    const buf = await res.arrayBuffer();
    return await new Promise<AudioBuffer>((resolve, reject) => {
      // decodeAudioData возвращает Promise, но мы его явно не используем,
      // полагаясь на колбэки для совместимости со старыми реализациями Web Audio
      void c.decodeAudioData(buf, resolve, reject);
    });
  }

  async function ensureSfxLoaded(key: SfxKey): Promise<void> {
    if (sfxBuffers[key]) return;
    const url = AUDIO_URLS.sfx[key];
    sfxBuffers[key] = await decodeUrl(url);
  }

  function playSfxNow(key: SfxKey): void {
    const c = ensureCtx();
    const buffer = sfxBuffers[key];
    if (!buffer) return;

    const source = c.createBufferSource();
    source.buffer = buffer;

    const gain = c.createGain();
    const vol = Math.max(0, Math.min(1, (SFX_VOLUME[key] ?? 1) * MASTER_VOLUME)) * (masterMuted ? 0 : 1);
    gain.gain.value = vol;

    source.connect(gain);
    gain.connect(c.destination);

    try {
      source.start(0);
    } catch {
      // Игнорируем единичный сбой запуска SFX
    }
  }

  async function loadBgmInternal(): Promise<void> {
    if (bgmBuffer) return;
    bgmBuffer = await decodeUrl(AUDIO_URLS.bgm);
  }

  function stopBgmInternal(): void {
    if (bgmSource) {
      try {
        bgmSource.stop(0);
      } catch {
        // Игнорируем ошибку остановки BGM при очистке
      }
      try {
        bgmSource.disconnect();
      } catch {
        // Игнорируем ошибку disconnect при очистке
      }
      bgmSource = null;
    }
    bgmPlaying = false;
  }

  async function startBgmInternal(): Promise<void> {
    await resumeCtx();
    await loadBgmInternal();
    if (!bgmBuffer || bgmPlaying) return;

    const c = ensureCtx();
    const src = c.createBufferSource();
    src.buffer = bgmBuffer;
    src.loop = true;

    const gain = c.createGain();
    const base = Math.max(0, Math.min(1, MUSIC_VOLUME * MASTER_VOLUME));
    gain.gain.value = masterMuted ? 0 : base;

    src.connect(gain);
    gain.connect(c.destination);

    try {
      src.start(0);
    } catch {
      // Игнорируем сбой запуска BGM: движок просто останется без фоновой музыки
    }

    bgmSource = src;
    bgmGain = gain;
    bgmPlaying = true;
  }

  singleton = {
    async unlock(): Promise<void> {
      await resumeCtx();
      unlocked = true;

      // На iOS/Safari иногда требуется реальный start() источника строго внутри жеста
      // Сыграем едва заметный «тихий» импульс, чтобы окончательно «пробудить» аудио‑подсистему
      try {
        const c = ensureCtx();
        const silentBuffer = c.createBuffer(1, 1, Math.max(22050, c.sampleRate));
        const src = c.createBufferSource();
        src.buffer = silentBuffer;
        const gain = c.createGain();
        gain.gain.value = 0.00001;
        src.connect(gain);
        gain.connect(c.destination);
        try {
          src.start(0);
        } catch {
          // Игнорируем сбой запуска «тихого» буфера
        }
        try {
          src.stop(0);
        } catch {
          // Игнорируем сбой остановки «тихого» буфера
        }
      } catch {
        // Даже если «тихий пинг» не удался, считаем контекст разблокированным
      }

      // «Тёплая» загрузка SFX (без await — в фоне)
      void ensureSfxLoaded('jump');
      void ensureSfxLoaded('blight');
      void ensureSfxLoaded('skeleton');
      void ensureSfxLoaded('coin');
      void ensureSfxLoaded('heart');
      // BGM подготовим лениво при первом старте

      // Если музыка уже была «заказана», пробуем стартануть её после unlock
      if (bgmShouldPlay && !bgmPlaying) {
        try {
          await startBgmInternal();
        } catch {
          // Игнорируем сбой запуска BGM после разблокировки
        }
      }
    },

    playSfx(key: SfxKey): void {
      // До пользовательского жеста (unlock) звук не проигрываем
      if (!unlocked) return;
      // Ограничиваем частоту вызовов, чтобы не накапливать слишком частые проигрывания
      const now = performance.now();
      const minGapMs = key === 'coin' || key === 'heart' ? 40 : 20;
      const last = sfxLastPlayMs[key] ?? 0;
      if (now - last < minGapMs) return;
      sfxLastPlayMs[key] = now;

      if (!sfxBuffers[key]) {
        // если ещё не декодировано — запустим фоном и сыграем после
        void ensureSfxLoaded(key).then(() => playSfxNow(key)).catch(() => {});
      } else {
        playSfxNow(key);
      }
    },

    async startBgm(): Promise<void> {
      bgmShouldPlay = true;
      if (!unlocked) return;
      try {
        await startBgmInternal();
      } catch {
        // Игнорируем ошибку запуска фоновой музыки
      }
    },

    stopBgm(): void {
      stopBgmInternal();
      bgmShouldPlay = false;
    },

    handleVisibility(hidden: boolean): void {
      // Игнорируем видимость, пока аудио не разблокировано
      if (!unlocked) return;
      if (hidden) {
        stopBgmInternal();
      } else {
        if (bgmShouldPlay) { void startBgmInternal(); }
      }
    },

    setMuted(muted: boolean): void {
      masterMuted = !!muted;
      // Сохраняем состояние mute
      try {
        localStorage.setItem('audio:muted', masterMuted ? '1' : '0');
      } catch {
        // Игнорируем ошибки доступа к хранилищу
      }
      // Обновляем текущий gain фоновой музыки, если он уже создан
      try {
        const base = Math.max(0, Math.min(1, MUSIC_VOLUME * MASTER_VOLUME));
        if (bgmGain) {
          bgmGain.gain.value = masterMuted ? 0 : base;
        }
      } catch {
        // Игнорируем ошибки изменения громкости
      }
    },

    isMuted(): boolean {
      return !!masterMuted;
    },
  };

  // Установим глобальный fallback для резюма контекста при жестах
  // Работает один раз и самоуничтожается после успешного resume
  // (вне объекта, но при первом создании движка)
  installGestureUnlockFallback();

  return singleton;
}
